/**
 * Copy for the "Delete this project?" confirmation on the Projects page and the
 * account page.
 *
 * This one is not cosmetic. `DELETE /api/projects/:id` (server/routes.ts) does two
 * different things to two different tables:
 *   - project_tasks: a HARD `.delete()` on every task in the project, right now;
 *   - projects:      a soft delete, `deleted_at` stamped, listed on /trash.
 * So "restore from Trash" brings the project back EMPTY. Every task, and with it
 * every comment, attachment and time-entry link hanging off those rows, is gone
 * the moment the button is clicked. The old confirmation was `Delete "<title>"?`
 * and said none of that.
 *
 * The recurrence line matters for the same reason the close dialog has one:
 * `spawnRecurringProjects` filters `.is("deleted_at", null)`, so deleting the
 * occurrence that holds `next_spawn_date` silently ends the chain — the same
 * class of bug as ENG-79, arrived at from the other direction.
 *
 * Counts come from the `GET /api/projects` aggregates
 * (`server/lib/projectTaskAggregate.ts`), the same source as the row's progress
 * bar, and unlike the close warning this one counts DONE tasks too — the delete
 * destroys finished work just as thoroughly as open work.
 *
 * Sentences render after "<title> will be deleted."
 */

export interface ProjectDeleteWarningInput {
  firm_task_total?: number;
  firm_task_done?: number;
  client_task_total?: number;
  client_task_done?: number;
  recurring_pattern?: string | null;
  next_spawn_date?: string | null;
}

export function buildProjectDeleteWarning(project: ProjectDeleteWarningInput): string[] {
  const lines: string[] = [];

  const hasAggregates =
    project.firm_task_total !== undefined || project.client_task_total !== undefined;
  const total = Math.max(0, project.firm_task_total ?? 0) + Math.max(0, project.client_task_total ?? 0);

  if (!hasAggregates) {
    // Don't invent a number. "Its tasks" is still the warning that matters.
    lines.push("Its tasks are deleted permanently — restoring the project brings it back empty.");
  } else if (total > 0) {
    const verb = total === 1 ? "task is" : "tasks are";
    lines.push(`Its ${total} ${verb} deleted permanently — restoring the project brings it back empty.`);
  }

  // Same guard as buildProjectCloseWarning: a clone inherits recurring_pattern, so
  // only next_spawn_date proves the chain is still live.
  if (project.recurring_pattern && project.next_spawn_date) {
    lines.push("This project recurs, and deleting it ends the chain — no further occurrences will be created.");
  }

  lines.push("It moves to Trash, where you can restore it for 30 days.");
  return lines;
}
